import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ArcElement, BarController, BarElement, CategoryScale, Chart, DoughnutController, Legend, LinearScale, Tooltip } from 'chart.js';
import { AbstractComponent, Attendance, AttendanceStatusEnum, ConfirmationComponent, DateUtil, ExportService, LeadSourceKpisResult, LeadSourceService, Pager } from 'lib-trend-core';
import { concatMap, debounceTime, distinctUntilChanged, finalize, Observable, Subject, takeUntil } from 'rxjs';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend, BarController, BarElement, CategoryScale, LinearScale);

const CHART_COLORS = [
  '#007bff', '#2fc2b1', '#f5a623', '#e91e8c', '#42a5f5',
  '#66bb6a', '#ef5350', '#ab47bc', '#ffa726', '#26c6da',
];

@Component({
    selector: 'app-report-lead-source',
    templateUrl: './report-lead-source.component.html',
    styleUrl: './report-lead-source.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportLeadSourceComponent extends AbstractComponent implements AfterViewInit {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  @ViewChild('chartDistributionByLeadSource', { static: false }) chartCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartConversionRate', { static: false }) conversionChartCanvas: ElementRef<HTMLCanvasElement>;
  isPicker: boolean = false;

  public result: LeadSourceKpisResult = null;

  public pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });

  public isLoading: boolean = false;
  public selectedFilters: { period?: string } = {};

  private termOfSearch: Subject<string> = new Subject<string>();
  private distributionChart: Chart | null = null;
  private conversionChart: Chart | null = null;
  private chartReady: boolean = false;

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcLeadSource' },
  ];

  constructor(
    injector: Injector,
    private leadSourceService: LeadSourceService,
    private cdr: ChangeDetectorRef,
    public exportService: ExportService,
    public dialog: MatDialog,
  ) {
    super(injector);
    this.setupForms();
  }

  ngOnInit(): void {
    this.getKpisLeadSource();
    this.getAttendacesByLeadSource();
  }

  ngAfterViewInit(): void {
    this.chartReady = true;
    if (this.result?.allLeadSources?.length) {
      this.buildDistributionChart();
      this.buildConversionChart();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.distributionChart?.destroy();
    this.distributionChart = null;
    this.conversionChart?.destroy();
    this.conversionChart = null;
  }

  private setupForms(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
    });

    this.selectedFilters.period = this.t('report.leadSourceReport.periodRange', {
      start: DateUtil.toFormart(thirtyDaysAgo, 'dd/MM/yyyy'),
      end: DateUtil.toFormart(today, 'dd/MM/yyyy'),
    });

    let previousValue = this.formGroup.value;
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((currentValue) => {
      if (
        previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
        previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd
      ) {
        previousValue = currentValue;
        const dateStart = currentValue.dateRange?.dateStart;
        const dateEnd = currentValue.dateRange?.dateEnd;
        if (dateStart && dateEnd) {
          this.selectedFilters.period = this.t('report.leadSourceReport.periodRange', {
            start: DateUtil.toFormart(dateStart, 'dd/MM/yyyy'),
            end: DateUtil.toFormart(dateEnd, 'dd/MM/yyyy'),
          });
        } else {
          delete this.selectedFilters.period;
        }
        this.getKpisLeadSource();
      }
    });

    this.termOfSearch.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pager.page = 1;
      this.getKpisLeadSource();
      this.getAttendacesByLeadSource();
    });
  }

  getKpisLeadSource() {
    this.loadingContent = true;
    const formValues = this.formGroup.getRawValue();
    const filters: Record<string, unknown> = {
      status: formValues.status,
      department: formValues.department,
      channel: formValues.channel,
      user: formValues.user,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.leadSourceService.getReportKpisLeadSource(this.searchParams, this.searchString)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingContent = false)
      )
      .subscribe({
        next: (response: Array<LeadSourceKpisResult>) => {
          this.result = response?.[0] ?? null;
          this.cdr.detectChanges();
          if (this.chartReady && this.chartCanvas?.nativeElement) {
            this.buildDistributionChart();
            this.buildConversionChart();
          }
        },
        error: (error) => {
          console.error('Erro ao obter KPIs de Origem do Lead:', error);
        }
      });
  }

  getAttendacesByLeadSource(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.leadSourceService.getReportAttendancesByLeadSource(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.pager = new Pager<Attendance>(response);
          this.loadingContent = false;
        },
        error: (error) => {
          console.error('Erro ao obter atendimentos por origem do lead:', error);
          this.loadingContent = false;
        }
      });
  }

  search(term: string) {
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  onPeriodChange(event: any) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      this.formGroup.get('dateRange')?.patchValue({
        dateStart: new Date(today.setDate(today.getDate() - period)),
        dateEnd: new Date(),
      });
      this.picker?.close();
    }
  }

  buildConversionChart(): void {
    if (!this.chartReady || !this.conversionChartCanvas?.nativeElement) return;

    const ctx = this.conversionChartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    this.conversionChart?.destroy();
    this.conversionChart = null;

    const sources = this.result?.allLeadSources;
    if (!sources?.length) return;

    const sorted = [...sources].sort((a, b) => b.conversionRate - a.conversionRate);

    const dataLabelsPlugin = {
      id: 'conversionDataLabels',
      afterDatasetsDraw(chart: Chart) {
        const { ctx: c, scales } = chart as any;
        const xScale = scales['x'];
        const yScale = scales['y'];
        const dataset = chart.data.datasets[0].data as number[];
        c.save();
        c.font = 'bold 11px sans-serif';
        c.fillStyle = '#333';
        c.textBaseline = 'middle';
        dataset.forEach((value: number, i: number) => {
          const xPos = xScale.getPixelForValue(value);
          const yPos = yScale.getPixelForValue(i);
          c.fillText(`${value}%`, xPos + 6, yPos);
        });
        c.restore();
      },
    };

    this.conversionChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: sorted.map(s => s.leadSourceName),
        datasets: [{
          data: sorted.map(s => s.conversionRate),
          backgroundColor: '#007bff',
          borderRadius: 4,
          barThickness: 18,
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 40 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.x}%`,
            },
          },
        },
        scales: {
          x: {
            min: 0,
            max: 100,
            ticks: {
              callback: (value) => `${value}%`,
              font: { size: 12 },
            },
            grid: { color: '#f0f0f0' },
          },
          y: {
            ticks: { font: { size: 13 } },
            grid: { display: false },
          },
        },
      },
      plugins: [dataLabelsPlugin],
    });
  }

  buildDistributionChart(): void {
    if (!this.chartReady || !this.chartCanvas?.nativeElement) return;

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    this.distributionChart?.destroy();
    this.distributionChart = null;

    const sources = this.result?.allLeadSources;
    if (!sources?.length) return;

    this.distributionChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: sources.map(s => s.leadSourceName),
        datasets: [{
          data: sources.map(s => s.totalAttendances),
          backgroundColor: sources.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 6,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { boxWidth: 12, padding: 16, font: { size: 13 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
            },
          },
        },
      },
    });
  }

  exportToExcel(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.leadSourceReport.confirmExportExcel'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isLoading = true;
      this.fetchAttendancesByLeadSourceRecursively([], new Pager<Attendance>({ perPage: 500 }))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (attendances: Array<Attendance>) => {
            const rows = this.formatAttendanceData(attendances);
            this.exportService
              .generateExcelLeadSourceReport(rows, this.result?.allLeadSources ?? [], this.result?.kpis, this.selectedFilters)
              .then((buffer: any) => this.downloadExcelFile(buffer))
              .catch((err: any) => this.handleError(err))
              .finally(() => {
                this.alertService.success(this.t('report.leadSourceReport.excelSuccess'));
                this.isLoading = false;
              });
          },
          error: (err) => {
            this.handleError(err);
            this.isLoading = false;
          },
        });
    });
  }

  exportToPDF(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.leadSourceReport.confirmExportPdf'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isLoading = true;
      this.fetchAttendancesByLeadSourceRecursively([], new Pager<Attendance>({ perPage: 500 }))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (attendances: Array<Attendance>) => {
            const rows = this.formatAttendanceData(attendances);
            this.exportService
              .generatePDFLeadSourceReport(rows, this.result?.allLeadSources ?? [], this.result?.kpis, this.selectedFilters)
              .then(() => this.alertService.success(this.t('report.leadSourceReport.pdfSuccess')))
              .catch((err: any) => this.handleError(err))
              .finally(() => {
                this.isLoading = false;
              });
          },
          error: (err) => {
            this.handleError(err);
            this.isLoading = false;
          },
        });
    });
  }

  private fetchAttendancesByLeadSourceRecursively(attendances: Attendance[], pager: Pager<Attendance>): Observable<Array<Attendance>> {
    return this.leadSourceService.getReportAttendancesByLeadSource(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap((newPager) => {
          const allAttendances = [...attendances, ...newPager.list];
          if (allAttendances.length >= newPager.total) {
            return new Observable<Array<Attendance>>(observer => {
              observer.next(allAttendances);
              observer.complete();
            });
          } else {
            return this.fetchAttendancesByLeadSourceRecursively(
              allAttendances,
              { ...pager, page: pager.page + 1 } as Pager<Attendance>,
            );
          }
        }),
      );
  }

  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trendmulti_Relatorio_Origem_Lead_${new Date().toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private handleError(err: any): void {
    this.alertService.error(err?.error?.message ?? this.t('report.leadSourceReport.unexpectedError'));
  }

  loadPage(page: number): void {
    this.pager.page = page;
    this.getAttendacesByLeadSource();

  }

  translateStatus(status: AttendanceStatusEnum): string {
    switch (status) {
      case AttendanceStatusEnum.IN_PROGRESS:
        return this.t('report.leadSourceReport.statusInProgress');
      case AttendanceStatusEnum.PENDING:
        return this.t('report.leadSourceReport.statusPending');
      case AttendanceStatusEnum.OVERTIME:
        return this.t('report.leadSourceReport.statusOvertime');
      case AttendanceStatusEnum.PAUSED:
        return this.t('report.leadSourceReport.statusPaused');
      case AttendanceStatusEnum.CLOSED:
        return this.t('report.leadSourceReport.statusClosed');
      default:
        return this.t('report.leadSourceReport.statusUnknown');
    }
  }

  private formatAttendanceData(attendances: Attendance[]) {
    return attendances.map((attendance: Attendance) => {
      const rawUser: any = attendance?.user;
      const user = Array.isArray(rawUser) ? rawUser[0] : rawUser;
      const leadSource = (attendance as any)?.leadSource ?? attendance?.contact?.leadSource;
      return {
        contact: attendance.contact,
        user,
        leadSource,
        status: super.getTranslatedStatusAttendance(attendance.status),
        createdAt: DateUtil.toFormart(attendance.createdAt, 'dd/MM/yyyy HH:mm:ss'),
      };
    });
  }
}
